import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Client } from '@stomp/stompjs';
import {
  setCricketInPlayGames,
  setSoccerInPlayGames,
  setTennisInPlayGames,
  setInplaySocketConnected,
  setCricketOdds,
  setSoccerOdds,
  setTennisOdds,
} from '../redux/features/gameSlice';
import {
  CRICKET_SERVICE_URL_SOCKET,
  SOCCER_AND_TENNIS_SERVICE_URL_SOCKET,
} from '../utils/constants';

// ---------------------------------------------------------------------------
// Network-quality detection
// ---------------------------------------------------------------------------
function getNetworkProfile() {
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!conn) return 'fast'; // assume fast when API unavailable

  // effectiveType: 'slow-2g' | '2g' | '3g' | '4g'
  const etype = conn.effectiveType;
  if (etype === 'slow-2g' || etype === '2g' || etype === '3g') return 'slow';
  // downlink < 1.5 Mbps → treat as slow even if reported as 4g
  if (typeof conn.downlink === 'number' && conn.downlink < 1.5) return 'slow';
  return 'fast';
}

function getConfig() {
  const slow = getNetworkProfile() === 'slow';
  return {
    reconnectDelay: slow ? 5000 : 3000,
    maxReconnectDelay: slow ? 60000 : 30000,
    heartbeatIncoming: slow ? 30000 : 15000,
    heartbeatOutgoing: slow ? 30000 : 15000,
    staleThreshold: slow ? 20000 : 8000,
    watchdogInterval: slow ? 8000 : 5000,
    stClientDelay: slow ? 3000 : 800, // stagger second client
  };
}

// ---------------------------------------------------------------------------
// Exponential backoff tracker
// ---------------------------------------------------------------------------
function createBackoff(baseMs, maxMs) {
  let attempt = 0;
  return {
    next() {
      const delay = Math.min(baseMs * Math.pow(2, attempt), maxMs);
      attempt++;
      return delay;
    },
    reset() { attempt = 0; },
  };
}

// ---------------------------------------------------------------------------
// RAF-based dispatch buffer — coalesces rapid payloads into one frame
// ---------------------------------------------------------------------------
function createBuffer(dispatchFn, actionCreator) {
  let buffer = null;
  let rafId = null;

  function add(data) {
    if (!buffer) {
      buffer = data;
    } else if (Array.isArray(data)) {
      buffer = data; // replace array payloads (match lists)
    } else {
      Object.assign(buffer, data); // merge object payloads (odds)
    }
    if (!rafId) {
      rafId = requestAnimationFrame(() => {
        dispatchFn(actionCreator(buffer));
        buffer = null;
        rafId = null;
      });
    }
  }

  function cancel() {
    if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
    buffer = null;
  }

  return { add, cancel };
}

// ---------------------------------------------------------------------------
// Client factories
// ---------------------------------------------------------------------------
function createCricketClient(dispatch, lastMsgRef, oddsBuffer, matchBuffer) {
  const cfg = getConfig();
  return new Client({
    brokerURL: CRICKET_SERVICE_URL_SOCKET,
    reconnectDelay: cfg.reconnectDelay,
    heartbeatIncoming: cfg.heartbeatIncoming,
    heartbeatOutgoing: cfg.heartbeatOutgoing,
    debug: () => null,
    onConnect: function () {
      lastMsgRef.current = Date.now();
      this.subscribe('/live/liveMatches', (message) => {
        try {
          lastMsgRef.current = Date.now();
          const data = JSON.parse(message.body);
          if (data && typeof data === 'object') {
            matchBuffer.add(data);
          }
        } catch (e) { console.warn('[WS] Cricket liveMatches parse error:', e.message); }
      });
      this.subscribe('/live/liveOdds', (message) => {
        try {
          lastMsgRef.current = Date.now();
          const data = JSON.parse(message.body);
          if (data && typeof data === 'object') {
            oddsBuffer.add(data);
          }
        } catch (e) { console.warn('[WS] Cricket liveOdds parse error:', e.message); }
      });
    },
    onStompError: (frame) => {
      console.warn('[WS] Cricket STOMP error:', frame.headers?.message || '');
    },
    onWebSocketClose: () => {
      console.warn('[WS] Cricket connection closed, will auto-reconnect');
    },
  });
}

function createSTClient(dispatch, lastMsgRef, soccerOddsBuffer, tennisOddsBuffer, soccerMatchBuffer, tennisMatchBuffer) {
  const cfg = getConfig();
  return new Client({
    brokerURL: SOCCER_AND_TENNIS_SERVICE_URL_SOCKET,
    reconnectDelay: cfg.reconnectDelay,
    heartbeatIncoming: cfg.heartbeatIncoming,
    heartbeatOutgoing: cfg.heartbeatOutgoing,
    debug: () => null,
    onConnect: function () {
      lastMsgRef.current = Date.now();
      this.subscribe('/live/liveMatches', (message) => {
        try {
          lastMsgRef.current = Date.now();
          const data = JSON.parse(message.body);
          if (data && typeof data === 'object') {
            if (data.tennisAllMatchesResponseDTOS) tennisMatchBuffer.add(data.tennisAllMatchesResponseDTOS);
            if (data.soccerAllMatchesResponseDTOS) soccerMatchBuffer.add(data.soccerAllMatchesResponseDTOS);
          }
        } catch (e) { console.warn('[WS] Soccer/Tennis liveMatches parse error:', e.message); }
      });
      this.subscribe('/live/soccer/liveOdds', (message) => {
        try {
          lastMsgRef.current = Date.now();
          const data = JSON.parse(message.body);
          if (data && typeof data === 'object') soccerOddsBuffer.add(data);
        } catch (e) { console.warn('[WS] Soccer liveOdds parse error:', e.message); }
      });
      this.subscribe('/live/tennis/liveOdds', (message) => {
        try {
          lastMsgRef.current = Date.now();
          const data = JSON.parse(message.body);
          if (data && typeof data === 'object') tennisOddsBuffer.add(data);
        } catch (e) { console.warn('[WS] Tennis liveOdds parse error:', e.message); }
      });
    },
    onStompError: (frame) => {
      console.warn('[WS] Soccer/Tennis STOMP error:', frame.headers?.message || '');
    },
    onWebSocketClose: () => {
      console.warn('[WS] Soccer/Tennis connection closed, will auto-reconnect');
    },
  });
}

// ---------------------------------------------------------------------------
// Reconnect helper with exponential backoff
// ---------------------------------------------------------------------------
function reconnectClient(label, clientsRef, key, backoff, createFn, ...createArgs) {
  try { clientsRef.current[key]?.deactivate(); } catch (e) {}
  const delay = backoff.next();
  console.warn(`[WS] ${label} reconnecting in ${delay}ms`);
  const timerId = setTimeout(() => {
    const fresh = createFn(...createArgs);
    clientsRef.current[key] = fresh;
    fresh.activate();
  }, delay);
  return timerId;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export default function useInplaySocket(enabled) {
  const dispatch = useDispatch();
  const clientsRef = useRef({ cricket: null, st: null });
  const lastCricketMsgRef = useRef(0);
  const lastSTMsgRef = useRef(0);
  const buffersRef = useRef(null);
  const reconnectTimersRef = useRef([]);

  useEffect(() => {
    if (!enabled) return;

    const cfg = getConfig();
    dispatch(setInplaySocketConnected(true));

    // Backoff trackers
    const cricketBackoff = createBackoff(cfg.reconnectDelay, cfg.maxReconnectDelay);
    const stBackoff = createBackoff(cfg.reconnectDelay, cfg.maxReconnectDelay);

    // Buffers — both odds AND match lists are buffered via RAF
    const cricketOddsBuffer = createBuffer(dispatch, setCricketOdds);
    const soccerOddsBuffer = createBuffer(dispatch, setSoccerOdds);
    const tennisOddsBuffer = createBuffer(dispatch, setTennisOdds);
    const cricketMatchBuffer = createBuffer(dispatch, setCricketInPlayGames);
    const soccerMatchBuffer = createBuffer(dispatch, setSoccerInPlayGames);
    const tennisMatchBuffer = createBuffer(dispatch, setTennisInPlayGames);
    buffersRef.current = {
      cricketOdds: cricketOddsBuffer, soccerOdds: soccerOddsBuffer, tennisOdds: tennisOddsBuffer,
      cricketMatch: cricketMatchBuffer, soccerMatch: soccerMatchBuffer, tennisMatch: tennisMatchBuffer,
    };

    // Create & activate clients (stagger the second one on slow networks)
    const cricketClient = createCricketClient(dispatch, lastCricketMsgRef, cricketOddsBuffer, cricketMatchBuffer);
    clientsRef.current.cricket = cricketClient;
    cricketClient.activate();
    cricketClient.onConnect = function () {
      cricketBackoff.reset(); // successful connect → reset backoff
      createCricketClient(dispatch, lastCricketMsgRef, cricketOddsBuffer, cricketMatchBuffer).onConnect.call(this);
    }.bind(cricketClient);

    const stActivationTimer = setTimeout(() => {
      const stClient = createSTClient(dispatch, lastSTMsgRef, soccerOddsBuffer, tennisOddsBuffer, soccerMatchBuffer, tennisMatchBuffer);
      clientsRef.current.st = stClient;
      stClient.activate();
    }, cfg.stClientDelay);
    reconnectTimersRef.current.push(stActivationTimer);

    // -----------------------------------------------------------------------
    // Staleness watchdog
    // -----------------------------------------------------------------------
    const watchdog = setInterval(() => {
      const now = Date.now();
      const threshold = getConfig().staleThreshold; // re-read in case network changed
      if (lastCricketMsgRef.current > 0 && now - lastCricketMsgRef.current > threshold) {
        console.warn('[WS] Cricket stale for', Math.round((now - lastCricketMsgRef.current) / 1000) + 's');
        lastCricketMsgRef.current = 0;
        const tid = reconnectClient('Cricket', clientsRef, 'cricket', cricketBackoff,
          createCricketClient, dispatch, lastCricketMsgRef, buffersRef.current.cricketOdds, buffersRef.current.cricketMatch);
        reconnectTimersRef.current.push(tid);
      }
      if (lastSTMsgRef.current > 0 && now - lastSTMsgRef.current > threshold) {
        console.warn('[WS] Soccer/Tennis stale for', Math.round((now - lastSTMsgRef.current) / 1000) + 's');
        lastSTMsgRef.current = 0;
        const tid = reconnectClient('Soccer/Tennis', clientsRef, 'st', stBackoff,
          createSTClient, dispatch, lastSTMsgRef, buffersRef.current.soccerOdds, buffersRef.current.tennisOdds, buffersRef.current.soccerMatch, buffersRef.current.tennisMatch);
        reconnectTimersRef.current.push(tid);
      }
    }, cfg.watchdogInterval);

    // -----------------------------------------------------------------------
    // Tab visibility — reconnect stale sockets when user returns
    // -----------------------------------------------------------------------
    const handleVisibilityChange = () => {
      if (document.hidden) return;
      const now = Date.now();
      const threshold = getConfig().staleThreshold;
      if (lastCricketMsgRef.current > 0 && now - lastCricketMsgRef.current > threshold) {
        console.warn('[WS] Cricket stale on tab return');
        lastCricketMsgRef.current = 0;
        const tid = reconnectClient('Cricket', clientsRef, 'cricket', cricketBackoff,
          createCricketClient, dispatch, lastCricketMsgRef, buffersRef.current.cricketOdds, buffersRef.current.cricketMatch);
        reconnectTimersRef.current.push(tid);
      }
      if (lastSTMsgRef.current > 0 && now - lastSTMsgRef.current > threshold) {
        console.warn('[WS] Soccer/Tennis stale on tab return');
        lastSTMsgRef.current = 0;
        const tid = reconnectClient('Soccer/Tennis', clientsRef, 'st', stBackoff,
          createSTClient, dispatch, lastSTMsgRef, buffersRef.current.soccerOdds, buffersRef.current.tennisOdds, buffersRef.current.soccerMatch, buffersRef.current.tennisMatch);
        reconnectTimersRef.current.push(tid);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // -----------------------------------------------------------------------
    // Online / offline — tear down when offline, reconnect when back online
    // -----------------------------------------------------------------------
    const handleOffline = () => {
      console.warn('[WS] Device went offline — deactivating sockets');
      dispatch(setInplaySocketConnected(false));
      try { clientsRef.current.cricket?.deactivate(); } catch (e) {}
      try { clientsRef.current.st?.deactivate(); } catch (e) {}
    };

    const handleOnline = () => {
      console.warn('[WS] Device back online — reconnecting');
      dispatch(setInplaySocketConnected(true));
      // Reset backoffs — fresh network, fresh start
      cricketBackoff.reset();
      stBackoff.reset();
      lastCricketMsgRef.current = 0;
      lastSTMsgRef.current = 0;

      const freshCricket = createCricketClient(dispatch, lastCricketMsgRef, buffersRef.current.cricketOdds, buffersRef.current.cricketMatch);
      clientsRef.current.cricket = freshCricket;
      freshCricket.activate();

      const delay = getConfig().stClientDelay;
      const tid = setTimeout(() => {
        const freshST = createSTClient(dispatch, lastSTMsgRef, buffersRef.current.soccerOdds, buffersRef.current.tennisOdds, buffersRef.current.soccerMatch, buffersRef.current.tennisMatch);
        clientsRef.current.st = freshST;
        freshST.activate();
      }, delay);
      reconnectTimersRef.current.push(tid);
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    // -----------------------------------------------------------------------
    // Cleanup
    // -----------------------------------------------------------------------
    return () => {
      clearInterval(watchdog);
      reconnectTimersRef.current.forEach(clearTimeout);
      reconnectTimersRef.current = [];
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      dispatch(setInplaySocketConnected(false));
      try { clientsRef.current.cricket?.deactivate(); } catch (e) {}
      try { clientsRef.current.st?.deactivate(); } catch (e) {}
      cricketOddsBuffer.cancel();
      soccerOddsBuffer.cancel();
      tennisOddsBuffer.cancel();
      cricketMatchBuffer.cancel();
      soccerMatchBuffer.cancel();
      tennisMatchBuffer.cancel();
    };
  }, [dispatch, enabled]);
}
